import { useEffect, useRef, useState } from "react";

type LazyHeroVideoProps = {
  /** Quelle des Videos (wird erst nach dem Laden der Seite gesetzt) */
  src: string;
  /** Standbild, das sofort angezeigt wird — identischer Bildausschnitt wie das Video */
  poster: string;
  className?: string;
  ariaLabel?: string;
  ariaHidden?: boolean;
};

/**
 * Hero-Video, das erst nach dem vollständigen Laden der Seite geladen wird.
 *
 * Bis dahin wird das Poster als Bild gerendert. Das verbessert LCP und
 * Datenvolumen auf mobilen Geräten deutlich, ohne die Optik zu verändern.
 * Nutzer mit aktiviertem Datensparmodus oder sehr langsamer Verbindung
 * bekommen dauerhaft nur das Standbild. Bei einem Ladefehler bleibt das
 * Poster als Fallback sichtbar.
 */
export function LazyHeroVideo({
  src,
  poster,
  className,
  ariaLabel,
  ariaHidden,
}: LazyHeroVideoProps) {
  const [showVideo, setShowVideo] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const connection = (
      navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string };
      }
    ).connection;
    if (connection?.saveData) return;
    if (connection?.effectiveType && /2g/.test(connection.effectiveType)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let timeout: number | undefined;
    const start = () => {
      timeout = window.setTimeout(() => setShowVideo(true), 300);
    };

    if (document.readyState === "complete") {
      start();
    } else {
      window.addEventListener("load", start, { once: true });
    }

    return () => {
      window.removeEventListener("load", start);
      if (timeout) window.clearTimeout(timeout);
    };
  }, []);

  if (!showVideo || videoFailed) {
    return (
      <img
        src={poster}
        alt={ariaHidden ? "" : (ariaLabel ?? "")}
        aria-hidden={ariaHidden}
        className={className}
        decoding="async"
        fetchPriority="high"
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className={className}
      src={src}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
      onError={() => setVideoFailed(true)}
    />
  );
}
